<template>
  <div class="action-log">
    <div class="action-log-header">
      <h3 class="gm-section-title">Action Log</h3>
      <button class="gm-btn gm-btn--sm gm-btn--ghost" @click="$emit('refresh')">Refresh</button>
    </div>

    <!-- Character Filter Component -->
    <ActionLogCharacterFilter
      v-if="characters && characters.length > 0"
      :characters="characters"
      :npc-characters="npcCharacters"
      @filter-change="handleFilterChange"
    />

    <div v-if="sortedCycleKeys.length > 0" class="action-list">
      <div v-for="cycleId in sortedCycleKeys" :key="cycleId" class="cycle-group">
        <div class="cycle-header">Cycle: {{ cycleId }}</div>
        <div class="cycle-actions">
          <ActionLogItem
              v-for="action in groupedActions[cycleId]"
              :key="action.id"
              :action="action"
              @characterSelected="handleCharacterSelect"
              :gmMode="gmMode"
          />
        </div>
      </div>
    </div>
    <div v-else class="gm-empty">
      <span class="gm-empty__title">No actions yet</span>
      <span class="gm-empty__hint">Actions will appear here as they happen.</span>
    </div>
  </div>
</template>

<script>
import ActionLogItem from "./ActionLogItem.vue";
import ActionLogCharacterFilter from "./ActionLogCharacterFilter.vue";

export default {
  name: "ActionLog",
  components: {
    ActionLogItem,
    ActionLogCharacterFilter,
  },
  props: {
    actions: {
      type: Array,
      required: true,
    },
    gmMode: {
      type: Boolean,
      default: false,
    },
    characters: {
      type: Array,
      default: () => [],
    },
    npcCharacters: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      selectedCharacterIds: [],
    };
  },
  computed: {
    filteredActions() {
      if (!this.selectedCharacterIds || this.selectedCharacterIds.length === 0) {
        return this.actions;
      }

      return this.actions.filter(action => {
        // Check if initiator matches any of the filter IDs
        if (this.selectedCharacterIds.includes(action.initiator.id)) {
          return true;
        }

        // Check if any target matches any of the filter IDs
        if (action.targets && action.targets.length > 0) {
          return action.targets.some(target => this.selectedCharacterIds.includes(target.id));
        }

        return false;
      });
    },
    groupedActions() {
      return this.filteredActions.reduce((groups, action) => {
        const cycleNumber = action.cycle.number;
        if (!groups[cycleNumber]) {
          groups[cycleNumber] = [];
        }
        groups[cycleNumber].push(action);
        return groups;
      }, {});
    },
    sortedCycleKeys() {
      return Object.keys(this.groupedActions)
          .map(Number)
          .sort((a, b) => b - a);
    },
  },
  emits: ["refresh", "characterSelected"],
  methods: {
    handleCharacterSelect(id) {
      this.$emit("characterSelected", id);
    },
    handleFilterChange(characterIds) {
      this.selectedCharacterIds = characterIds;
    },
  },
};
</script>

<style scoped>
.action-log {
  min-width: 0;
}

.action-log-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
  padding-bottom: var(--gm-gap-xs);
  background: var(--gm-surface-1);
}

.action-list {
  display: flex;
  flex-direction: column;
}

.cycle-group {
  margin-bottom: var(--gm-gap);
}

.cycle-header {
  font-family: var(--gm-font-display);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gm-text-muted);
  margin-bottom: var(--gm-gap-xs);
  border-bottom: 1px solid var(--gm-border-dim);
  padding-bottom: var(--gm-gap-xs);
}

.cycle-actions {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-xs);
}
</style>
